import React, { useMemo, useState } from "react";

import {
  AUDIT_TYPE_AUDITOR_MAPPING,
  AUDITOR_TYPE_LABELS,
  AuditType,
  AuditorType,
} from "../../../api/audits";

import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import type { OptionNameValuePair } from "../../../lib/interfaces/types";
import ManagementModal from "../../../components/ui/modals/ManagementModal";

interface ConductedByModalProps {
  auditType: AuditType;
  auditorType?: AuditorType;
  auditorName?: string | null;
  onChange: (value: {
    auditor_type: AuditorType;
    auditor_name?: string | null;
  }) => void;
  onClose: () => void;
}

const ConductedByModal: React.FC<ConductedByModalProps> = ({
  auditType,
  auditorType,
  auditorName,
  onChange,
  onClose,
}) => {

  const [draft, setDraft] = useState<{auditor_type?: AuditorType; auditor_name?: string | null;}>({
    auditor_type: auditorType,
    auditor_name: auditorName,
  });

  const auditorTypeOptions = useMemo<OptionNameValuePair<AuditorType>[]>(
    () =>
      AUDIT_TYPE_AUDITOR_MAPPING[auditType].map((type) => ({
        name: AUDITOR_TYPE_LABELS[type],
        value: type,
      })),
    [auditType],
  );

  const handleApply = () => {
    if (!draft.auditor_type) return;

    onChange({
      auditor_type: draft.auditor_type,
      auditor_name: draft.auditor_name?.trim(),
    });

    onClose();
  };

  return (
    <ManagementModal
      title="Conducted By"
      submitLabel="Apply"
      cancelLabel="Discard"
      onSubmit={handleApply}
      onCancel={onClose}
      onClose={onClose}
      submitEnabled={!!draft.auditor_type}
    >
      <div className="flex w-full min-w-0 flex-col gap-6">
        <div className="flex w-full min-w-0 flex-col gap-2">
          <label className="text-[14px] font-medium leading-none text-[#0A0A0A]">
            Auditor Type<span className="text-[#DC2626]">*</span>
          </label>

          <SelectDropdownV2
            name="auditor type"
            options={auditorTypeOptions}
            selectedOption={auditorTypeOptions.find((option) => option.value === draft.auditor_type)}
            onSelectionChange={(selected) =>
              setDraft((current) => ({
                ...current,
                auditor_type: selected.value,
              }))
            }
            containerClassName="w-full min-w-0"
            labelClassName="flex h-9 w-full min-w-0 items-center justify-between rounded-[8px] border border-[#E5E5E5] bg-white px-3 py-1.5 text-[14px] text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
            dropdownClassName="!w-full !min-w-0 !max-w-[554px] box-border"
            optionClassName="!whitespace-normal !break-words hover:!bg-transparent"
          />
        </div>

        <div className="flex w-full min-w-0 flex-col gap-2">
          <label className="text-[14px] font-medium leading-none text-[#0A0A0A]">
            Name
          </label>

          <input
            type="text"
            value={draft.auditor_name ?? ""}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                auditor_name: event.target.value,
              }))
            }
            placeholder="Enter name of auditor"
            className="h-9 w-full min-w-0 rounded-[8px] border border-[#E5E5E5] bg-white px-3 py-1.5 text-[14px] font-normal leading-5 text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] outline-none placeholder:text-[#737373]"
          />
        </div>
      </div>
    </ManagementModal>
  );
};

export default ConductedByModal;